import { LoadingSpinner } from "components/LoadingSpinner";
import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Dialog } from "primereact/dialog";
import { Message } from "primereact/message";
import { Tag } from "primereact/tag";
import styled from "styled-components";
import {
  IBenefitPreviewExample,
  IBenefitPreviewResult,
} from "types/BenefitSelection/BenefitSelection";

import { BENEFIT_LABELS } from "./constants";
import { Surface } from "./Surface";

const StyledMessage = styled(Message)`
  justify-content: flex-start;
`;

const StyledDataTable = styled(DataTable)`
  .p-datatable-thead > tr > th {
    background: #ffffff;
  }
  .p-datatable-tbody > tr > td {
    font-size: 0.85rem;
  }
`;

const Tile = styled.div`
  display: flex;
  flex-direction: column;
  padding: 0.6rem 0.9rem;
  border-radius: 6px;
  background: var(--surface-100);
  min-width: 7.5rem;

  strong {
    font-size: 1.15rem;
  }

  span {
    font-size: 0.75rem;
    color: var(--text-color-secondary);
  }
`;

/**
 * A benefit's value as the preview reports it.
 *
 * ⚠️ `null` IS "NO VALUE", NOT ZERO, and the two mean different things to a patient: a blank copay
 * is one nobody could read, a `$0` copay is one the payer answered. Rendering both as "—" would hide
 * exactly the change this dialog exists to show.
 */
const formatValue = (value: number | null, benefit: string): string => {
  if (value === null || value === undefined) return "—";
  return benefit === "COINSURANCE_PERCENT"
    ? `${value}%`
    : `$${value.toLocaleString()}`;
};

const nowCellBody = (row: IBenefitPreviewExample) => (
  <span>{formatValue(row.now, row.benefit)}</span>
);

const afterCellBody = (row: IBenefitPreviewExample) => (
  <span className="font-medium">{formatValue(row.after, row.benefit)}</span>
);

const dateCellBody = (row: IBenefitPreviewExample) => (
  <span>{new Date(row.date).toLocaleDateString()}</span>
);

const requestIdCellBody = (row: IBenefitPreviewExample) => (
  <span>{row.pverifyRequestId}</span>
);

const payerCellBody = (row: IBenefitPreviewExample) => (
  <span>
    {row.payerName ?? "—"}
    {row.planName ? ` · ${row.planName}` : ""}
  </span>
);

const benefitCellBody = (row: IBenefitPreviewExample) => (
  <span>{BENEFIT_LABELS[row.benefit] ?? row.benefit}</span>
);

const quotedCellBody = (row: IBenefitPreviewExample) => {
  if (row.quotedToPatient === null || row.quotedToPatient === undefined) {
    return <span className="text-color-secondary">Unknown</span>;
  }
  return (
    <Tag
      value={row.quotedToPatient ? "Yes" : "No"}
      severity={row.quotedToPatient ? "warning" : undefined}
      className={row.quotedToPatient ? "" : "surface-200 text-color"}
    />
  );
};

interface IPreviewDialogProps {
  visible: boolean;
  onHide: () => void;
  result?: IBenefitPreviewResult;
  isLoading: boolean;
  /** True when the endpoint is not deployed yet — PR 3 builds it. */
  isUnavailable: boolean;
  isError: boolean;
  saving: boolean;
  onSave: () => void;
}

/**
 * CLE-2792 — what the unsaved configuration would have done to the checks already stored.
 *
 * 🚨 BUILT AGAINST PR 3's CONTRACT, WHICH IS NOT MERGED. Until it is, the call 404s and this dialog
 * says the preview is not available — it does not fabricate counts. A preview showing invented
 * numbers beside a Save button is the worst thing on this screen.
 *
 * ⚠️ THE HISTORY RUNG IS NOT SIMULATED, and the endpoint says so in its own `notes`. Those notes are
 * rendered verbatim rather than restated here: a caveat maintained in two places goes stale in one.
 */
export function PreviewDialog({
  visible,
  onHide,
  result,
  isLoading,
  isUnavailable,
  isError,
  saving,
  onSave,
}: IPreviewDialogProps) {
  const perBenefit = Object.entries(result?.perBenefit ?? {});

  return (
    <Dialog
      header="Preview"
      visible={visible}
      onHide={onHide}
      style={{ width: "min(64rem, 96vw)" }}
      data-testid="benefitSelection.previewDialog"
      footer={
        <div className="flex justify-content-end gap-2">
          <Button
            type="button"
            label="Close"
            className="p-button-text p-button-sm"
            data-testid="benefitSelection.previewDialog.close"
            onClick={onHide}
          />
          <Button
            className="p-button-sm"
            type="button"
            label="Save these changes"
            loading={saving}
            data-testid="benefitSelection.previewDialog.save"
            onClick={onSave}
          />
        </div>
      }
    >
      <Surface className="flex flex-column gap-3">
        {isLoading && <LoadingSpinner />}

        {isUnavailable && (
          <StyledMessage
            severity="warn"
            data-testid="benefitSelection.previewDialog.unavailable"
            text="Preview is not available on this environment yet. The configuration can still be saved; nothing about the save depends on this dialog."
          />
        )}

        {isError && !isUnavailable && (
          <StyledMessage
            severity="error"
            data-testid="benefitSelection.previewDialog.error"
            text="The preview could not be run."
          />
        )}

        {result && (
          <>
            <span className="text-sm text-color-secondary">
              Your unsaved changes against the last {result.window.responses}{" "}
              stored checks
              {result.window.capped ? " (capped)" : ""}.
            </span>

            <div className="flex flex-wrap gap-3">
              {perBenefit.map(([benefit, counts]) => (
                <div
                  key={benefit}
                  className="flex flex-column gap-2 p-3 border-round border-1 border-gray-200"
                  data-testid={`benefitSelection.previewDialog.${benefit}`}
                >
                  <span className="font-medium text-sm">
                    {BENEFIT_LABELS[benefit] ?? benefit}
                  </span>
                  <div className="flex gap-2">
                    <Tile>
                      <strong>{counts.same}</strong>
                      <span>same answer</span>
                    </Tile>
                    <Tile>
                      <strong>{counts.changed}</strong>
                      <span>different amount</span>
                    </Tile>
                    <Tile>
                      <strong>{counts.newlyBlank}</strong>
                      <span>would go blank</span>
                    </Tile>
                    <Tile>
                      <strong>{counts.newlyAnswered}</strong>
                      <span>newly answered</span>
                    </Tile>
                  </div>
                </div>
              ))}
            </div>

            <span className="font-medium text-sm">
              Checks that would change{" "}
              <span className="text-color-secondary font-normal">
                {result.examples.length} shown
              </span>
            </span>

            <StyledDataTable
              value={result.examples}
              dataKey="pverifyResponseId"
              emptyMessage="No stored check changes under these settings."
              data-testid="benefitSelection.previewDialog.examples"
            >
              <Column header="Date" body={dateCellBody} />
              <Column header="pVerify RequestID" body={requestIdCellBody} />
              <Column header="Payer · plan" body={payerCellBody} />
              <Column header="Benefit" body={benefitCellBody} />
              <Column header="Now" body={nowCellBody} />
              <Column header="After" body={afterCellBody} />
              <Column header="Quoted to a patient" body={quotedCellBody} />
            </StyledDataTable>

            {result.notes.map((note) => (
              <StyledMessage
                key={note}
                severity="info"
                data-testid="benefitSelection.previewDialog.note"
                text={note}
              />
            ))}
          </>
        )}
      </Surface>
    </Dialog>
  );
}
